import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Mail,
  Send,
  Plus,
  RefreshCw,
  Calendar,
  Users,
  BarChart3,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Pause,
  Play,
  Eye,
  Pencil,
  Trash2,
  Sparkles,
  TrendingUp,
  MousePointer
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminEmailMarketing() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState("campaigns");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    subject: "",
    content: "",
    template_id: "",
    scheduled_at: ""
  });

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem("speakr_admin_token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [campaignsRes, templatesRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/email/campaigns`, { headers }),
        fetch(`${API_URL}/api/admin/email/templates`, { headers }),
        fetch(`${API_URL}/api/admin/email/analytics?days=30`, { headers })
      ]);

      if (campaignsRes.ok) {
        const data = await campaignsRes.json();
        setCampaigns(data.campaigns || []);
      }
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates || []);
      }
      if (analyticsRes.ok) {
        setAnalytics(await analyticsRes.json());
      }
    } catch (error) {
      toast.error("Failed to load email marketing data");
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async () => {
    if (!newCampaign.name || !newCampaign.subject || !newCampaign.content) {
      toast.error("Please fill in all required fields");
      return;
    }

    const token = localStorage.getItem("speakr_admin_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/email/campaigns`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newCampaign)
      });

      if (res.ok) {
        toast.success("Campaign created successfully");
        setShowCreateDialog(false);
        setNewCampaign({ name: "", subject: "", content: "", template_id: "", scheduled_at: "" });
        loadAllData();
      } else {
        toast.error("Failed to create campaign");
      }
    } catch (error) {
      toast.error("Failed to create campaign");
    }
  };

  const sendCampaign = async (campaignId) => {
    const token = localStorage.getItem("speakr_admin_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/email/campaigns/${campaignId}/send`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success("Campaign sent!");
        loadAllData();
      } else {
        toast.error("Failed to send campaign");
      }
    } catch (error) {
      toast.error("Failed to send campaign");
    }
  };

  const scheduleCampaign = async (campaignId, scheduledAt) => {
    const token = localStorage.getItem("speakr_admin_token");
    try {
      const res = await fetch(`${API_URL}/api/admin/email/campaigns/${campaignId}/schedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ scheduled_at: scheduledAt })
      });

      if (res.ok) {
        toast.success("Campaign scheduled");
        loadAllData();
      } else {
        toast.error("Failed to schedule campaign");
      }
    } catch (error) {
      toast.error("Failed to schedule campaign");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { class: "bg-slate-100 text-slate-800", icon: FileText },
      scheduled: { class: "bg-blue-100 text-blue-800", icon: Calendar },
      sending: { class: "bg-amber-100 text-amber-800", icon: RefreshCw },
      sent: { class: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
      paused: { class: "bg-orange-100 text-orange-800", icon: Pause },
      cancelled: { class: "bg-red-100 text-red-800", icon: XCircle }
    };
    const v = variants[status] || variants.draft;
    const Icon = v.icon;
    return (
      <Badge className={`${v.class} gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const getCategoryBadge = (category) => {
    const colors = {
      onboarding: "bg-emerald-100 text-emerald-800",
      lifecycle: "bg-blue-100 text-blue-800",
      product: "bg-purple-100 text-purple-800",
      education: "bg-amber-100 text-amber-800",
      marketing: "bg-pink-100 text-pink-800",
      newsletter: "bg-indigo-100 text-indigo-800",
      retention: "bg-red-100 text-red-800"
    };
    return <Badge className={colors[category] || "bg-slate-100 text-slate-800"}>{category}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-email-marketing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Email Marketing</h1>
          <p className="text-slate-500 mt-1">Campaigns, automation & analytics</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadAllData} variant="outline" data-testid="refresh-email">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-campaign-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Email Campaign</DialogTitle>
                <DialogDescription>Design and schedule your email campaign</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Campaign Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., January Newsletter"
                      value={newCampaign.name}
                      onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                      data-testid="campaign-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="template">Template</Label>
                    <Select
                      value={newCampaign.template_id}
                      onValueChange={(v) => setNewCampaign({ ...newCampaign, template_id: v })}
                    >
                      <SelectTrigger data-testid="template-select">
                        <SelectValue placeholder="Select template" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No template</SelectItem>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject Line *</Label>
                  <Input
                    id="subject"
                    placeholder="e.g., Your monthly Speakr update"
                    value={newCampaign.subject}
                    onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
                    data-testid="campaign-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">Email Content (HTML) *</Label>
                  <Textarea
                    id="content"
                    placeholder="<h1>Hello!</h1><p>Your email content here...</p>"
                    rows={8}
                    value={newCampaign.content}
                    onChange={(e) => setNewCampaign({ ...newCampaign, content: e.target.value })}
                    data-testid="campaign-content-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="schedule">Schedule (Optional)</Label>
                  <Input
                    id="schedule"
                    type="datetime-local"
                    value={newCampaign.scheduled_at}
                    onChange={(e) => setNewCampaign({ ...newCampaign, scheduled_at: e.target.value })}
                    data-testid="campaign-schedule-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                <Button onClick={createCampaign} data-testid="save-campaign-btn">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create Campaign
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Analytics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="total-campaigns-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Campaigns</p>
                <p className="text-3xl font-bold text-slate-900">{analytics?.campaigns?.total || 0}</p>
                <p className="text-sm text-emerald-600 mt-1">
                  {analytics?.campaigns?.sent || 0} sent
                </p>
              </div>
              <div className="p-2.5 rounded-lg bg-blue-100">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="emails-sent-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Emails Sent (30d)</p>
                <p className="text-3xl font-bold text-slate-900">{analytics?.emails?.sent_period || 0}</p>
                <p className="text-sm text-slate-500 mt-1">Last 30 days</p>
              </div>
              <div className="p-2.5 rounded-lg bg-emerald-100">
                <Send className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="active-subscribers-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Subscribers</p>
                <p className="text-3xl font-bold text-slate-900">{analytics?.subscribers?.active || 0}</p>
                <p className="text-sm text-emerald-600 mt-1">Double opt-in</p>
              </div>
              <div className="p-2.5 rounded-lg bg-purple-100">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="avg-open-rate-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Avg Open Rate</p>
                <p className="text-3xl font-bold text-slate-900">--</p>
                <p className="text-sm text-slate-500 mt-1">Coming soon</p>
              </div>
              <div className="p-2.5 rounded-lg bg-amber-100">
                <TrendingUp className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns ({campaigns.length})</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="automation" data-testid="tab-automation">Automation</TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Campaigns</CardTitle>
              <CardDescription>Manage your email marketing campaigns</CardDescription>
            </CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 mb-4">No campaigns yet</p>
                  <Button onClick={() => setShowCreateDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Campaign
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {campaigns.map((campaign) => (
                    <div key={campaign.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50" data-testid={`campaign-${campaign.id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{campaign.name}</p>
                          {getStatusBadge(campaign.status)}
                        </div>
                        <p className="text-sm text-slate-500">{campaign.subject}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Created: {new Date(campaign.created_at).toLocaleDateString()}
                          {campaign.sent_at && ` | Sent: ${new Date(campaign.sent_at).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Stats */}
                        {campaign.status === "sent" && (
                          <div className="flex items-center gap-4 text-sm text-slate-500 mr-4">
                            <span title="Sent"><Send className="w-4 h-4 inline mr-1" />{campaign.stats?.sent || 0}</span>
                            <span title="Opened"><Eye className="w-4 h-4 inline mr-1" />{campaign.stats?.opened || 0}</span>
                            <span title="Clicked"><MousePointer className="w-4 h-4 inline mr-1" />{campaign.stats?.clicked || 0}</span>
                          </div>
                        )}
                        {/* Actions */}
                        {campaign.status === "draft" && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => sendCampaign(campaign.id)}>
                              <Send className="w-4 h-4 mr-1" />
                              Send Now
                            </Button>
                          </div>
                        )}
                        {campaign.status === "scheduled" && (
                          <Badge className="bg-blue-100 text-blue-800">
                            <Calendar className="w-3 h-3 mr-1" />
                            {new Date(campaign.scheduled_at).toLocaleString()}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Templates</CardTitle>
              <CardDescription>Pre-built templates for common email types</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <div key={template.id} className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer" data-testid={`template-${template.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">{template.name}</p>
                      {getCategoryBadge(template.category)}
                    </div>
                    <p className="text-sm text-slate-500">{template.subject}</p>
                    <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => {
                      setNewCampaign({
                        ...newCampaign,
                        template_id: template.id,
                        subject: template.subject
                      });
                      setShowCreateDialog(true);
                    }}>
                      Use Template
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Automation Tab */}
        <TabsContent value="automation" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Email Automation</CardTitle>
              <CardDescription>Automated email sequences and triggers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Welcome Series */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium">Welcome Series</p>
                      <p className="text-sm text-slate-500">Sent to new users after signup</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                </div>
                <div className="flex gap-2 ml-13">
                  <Badge variant="outline">Day 0: Welcome</Badge>
                  <Badge variant="outline">Day 3: Tips</Badge>
                  <Badge variant="outline">Day 7: Features</Badge>
                </div>
              </div>

              {/* Trial Reminders */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">Trial Reminders</p>
                      <p className="text-sm text-slate-500">Remind users before trial ends</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                </div>
                <div className="flex gap-2 ml-13">
                  <Badge variant="outline">7 days left</Badge>
                  <Badge variant="outline">3 days left</Badge>
                  <Badge variant="outline">1 day left</Badge>
                </div>
              </div>

              {/* Re-engagement */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium">Win-back Campaign</p>
                      <p className="text-sm text-slate-500">Re-engage churned users</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                </div>
                <div className="flex gap-2 ml-13">
                  <Badge variant="outline">Day 7: We miss you</Badge>
                  <Badge variant="outline">Day 30: Special offer</Badge>
                </div>
              </div>

              {/* Usage Alerts */}
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">Usage Alerts</p>
                      <p className="text-sm text-slate-500">Notify users about usage limits</p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                </div>
                <div className="flex gap-2 ml-13">
                  <Badge variant="outline">80% usage</Badge>
                  <Badge variant="outline">100% usage</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
